import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserStatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  Search, Eye, CheckCircle2, XCircle, Ban, Trash2, ChevronUp, ChevronDown, Power,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const PAGE_SIZE = 8;

function PaginationBar({ page, setPage, total }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div className="flex items-center justify-between mt-4 text-xs text-slate-600 dark:text-slate-400">
      <div className="font-mono-tech">
        Showing {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
        {Math.min(page * PAGE_SIZE, total)} of {total}
      </div>
      <div className="flex items-center gap-2">
        <Button
          data-testid="pagination-prev"
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
          className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
        >
          Prev
        </Button>
        <span className="font-mono-tech">
          {page} / {totalPages}
        </span>
        <Button
          data-testid="pagination-next"
          size="sm"
          variant="outline"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
          className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export default function StoreOwners({ status }) {
  const isPending = status === "PENDING";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/store-owners", { params: { status, search: search || undefined } })
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = (a[sort.key] || "").toString();
      const bv = (b[sort.key] || "").toString();
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [rows, sort]);

  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const onAction = async (id, action) => {
    try {
      if (action === "delete") {
        if (!confirm("Delete this store owner?")) return;
        await api.delete(`/admin/store-owners/${id}`);
        toast.success("Store owner deleted.");
      } else {
        await api.post(`/admin/store-owners/${id}/${action}`);
        toast.success(`Store owner ${action}d.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const SortHeader = ({ k, children }) => (
    <button
      data-testid={`sort-${k}`}
      onClick={() => toggleSort(k)}
      className="inline-flex items-center gap-1 hover:text-slate-900 hover:dark:text-white"
    >
      {children}
      {sort.key === k ? (
        sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
      ) : null}
    </button>
  );

  return (
    <div data-testid="store-owners-page" className="space-y-6">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">
          / {isPending ? "Pending Approval" : "Registered Store Owners"}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {isPending ? "Store Owners Pending Approval" : "Registered Store Owners"}
        </h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="search-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, store, email…"
            className="pl-10 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus-visible:ring-cyan-400"
          />
        </div>
        <div className="text-xs font-mono-tech text-slate-500">
          {loading ? "Loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-slate-100/40 dark:bg-slate-900/40">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-100/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">
              <tr>
                <th className="text-left px-5 py-3"><SortHeader k="owner_name">Owner</SortHeader></th>
                <th className="text-left px-5 py-3"><SortHeader k="store_name">Store</SortHeader></th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Phone</th>
                <th className="text-left px-5 py-3">Business Address</th>
                <th className="text-left px-5 py-3"><SortHeader k="created_at">{isPending ? "Submitted" : "Registered"}</SortHeader></th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`store-owner-row-${r.id}`}
                  className="border-b border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-100/60 hover:dark:bg-slate-900/60 transition-colors"
                >
                  <td className="px-5 py-3 text-slate-900 dark:text-white font-medium">{r.owner_name}</td>
                  <td className="px-5 py-3 text-slate-700 dark:text-slate-300">{r.store_name}</td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{r.email}</td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{r.phone}</td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-400 max-w-[220px] truncate">{r.business_address}</td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3"><UserStatusBadge status={r.status} /></td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        data-testid={`view-store-owner-${r.id}`}
                        size="icon"
                        variant="ghost"
                        title="View"
                        onClick={() => setViewing(r)}
                        className="h-8 w-8 text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {isPending ? (
                        <>
                          <Button
                            data-testid={`approve-store-owner-${r.id}`}
                            size="icon"
                            variant="ghost"
                            title="Approve"
                            onClick={() => onAction(r.id, "approve")}
                            className="h-8 w-8 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`reject-store-owner-${r.id}`}
                            size="icon"
                            variant="ghost"
                            title="Reject"
                            onClick={() => onAction(r.id, "reject")}
                            className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {r.status === "SUSPENDED" ? (
                            <Button
                              data-testid={`activate-store-owner-${r.id}`}
                              size="icon"
                              variant="ghost"
                              title="Activate"
                              onClick={() => onAction(r.id, "activate")}
                              className="h-8 w-8 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            >
                              <Power className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              data-testid={`suspend-store-owner-${r.id}`}
                              size="icon"
                              variant="ghost"
                              title="Suspend"
                              onClick={() => onAction(r.id, "suspend")}
                              className="h-8 w-8 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            data-testid={`delete-store-owner-${r.id}`}
                            size="icon"
                            variant="ghost"
                            title="Delete"
                            onClick={() => onAction(r.id, "delete")}
                            className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && paged.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center text-slate-500">
                    <div className="text-base font-display font-semibold mb-1 text-slate-700 dark:text-slate-300">No records found</div>
                    <div className="text-xs">Try adjusting your search or check back later.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PaginationBar page={page} setPage={setPage} total={sorted.length} />

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Store Owner Details</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Full registration record.
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              {[
                ["Owner", viewing.owner_name],
                ["Store", viewing.store_name],
                ["Email", viewing.email],
                ["Phone", viewing.phone],
                ["Business Address", viewing.business_address],
                ["Registered", new Date(viewing.created_at).toLocaleString()],
                ["Status", viewing.status],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3 py-2 border-b border-slate-200/60 dark:border-slate-800/60">
                  <div className="text-slate-500 text-xs font-mono-tech uppercase tracking-wider">{k}</div>
                  <div className="col-span-2 text-slate-900 dark:text-white">{v}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
